export interface ReadingPosition {
  messageId: string | null
  offset: number
  bottom: boolean
  detached: boolean
}

const accounts = new Map<string, Record<string, ReadingPosition>>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()
const key = (userId: string) => `klichat.reading.${userId}`

function positions(userId: string): Record<string, ReadingPosition> {
  let data = accounts.get(userId)
  if (!data) {
    data = {}
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key(userId)) ?? '{}')
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [channelId, value] of Object.entries(parsed).slice(-100)) {
          if (!value || typeof value !== 'object') continue
          const p = value as ReadingPosition
          if ((p.messageId === null || typeof p.messageId === 'string' && /^\d+$/.test(p.messageId)) && Number.isFinite(p.offset) && typeof p.bottom === 'boolean' && typeof p.detached === 'boolean') data[channelId] = p
        }
      }
    } catch { /* Storage can be blocked; positions still work for this session. */ }
    accounts.set(userId, data)
  }
  return data
}

export function readPosition(userId: string | undefined, channelId: string): ReadingPosition | undefined {
  return userId ? positions(userId)[channelId] : undefined
}

export function flushReading(userId: string | undefined): void {
  if (!userId) return
  clearTimeout(timers.get(userId))
  timers.delete(userId)
  try { localStorage.setItem(key(userId), JSON.stringify(positions(userId))) } catch { /* Memory fallback. */ }
}

export function savePosition(userId: string | undefined, channelId: string, position: ReadingPosition): void {
  if (!userId) return
  const data = positions(userId)
  delete data[channelId]
  data[channelId] = position
  for (const id of Object.keys(data).slice(0, -100)) delete data[id]
  clearTimeout(timers.get(userId))
  timers.set(userId, setTimeout(() => flushReading(userId), 200))
}

/** First visible message, including its partially hidden top. No message text is stored. */
export function readingAnchor(el: HTMLElement): Pick<ReadingPosition, 'messageId' | 'offset'> {
  const top = el.getBoundingClientRect().top
  const rows = el.querySelectorAll<HTMLElement>('[data-message-id]')
  let low = 0
  let high = rows.length
  while (low < high) {
    const mid = (low + high) >>> 1
    if (rows[mid]!.getBoundingClientRect().bottom <= top) low = mid + 1
    else high = mid
  }
  const row = rows[low]
  const id = row?.dataset.messageId
  return { messageId: id && /^\d+$/.test(id) ? id : null, offset: row ? row.getBoundingClientRect().top - top : 0 }
}

export function restoreAnchor(el: HTMLElement, anchor: Pick<ReadingPosition, 'messageId' | 'offset'>): boolean {
  if (!anchor.messageId) return false
  const rows = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
  // A deleted message falls back to the next surviving neighbour.
  const row = rows.find((r) => r.dataset.messageId === anchor.messageId) ?? rows.find((r) => {
    const id = r.dataset.messageId
    return id && /^\d+$/.test(id) && BigInt(id) > BigInt(anchor.messageId!)
  }) ?? rows.at(-1)
  if (!row) return false
  el.scrollTop += row.getBoundingClientRect().top - el.getBoundingClientRect().top - anchor.offset
  return true
}
